import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import type { SelectProps } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { deriveHeroFrom, deriveHeroTo } from 'app/theme';
import { ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { HeroActionButton, HeroRoot as HeroSection } from 'shared/components/HeroContainer';

export const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const FILTER_FIELD_BG_TRANSLUCENT = 'rgba(248, 246, 253, 0.6)';
const FILTER_FIELD_BORDER = 'rgba(34, 28, 90, 0.10)';
const FILTER_PLACEHOLDER = '#8782A0';
const CARD_BORDER = 'rgba(34, 28, 90, 0.07)';

export const FilterToolbarCard = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 16,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 14,
  backgroundColor: '#fff',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  padding: '12px 20px',
});

export const SearchFieldBox = styled(Box)({
  position: 'relative',
  flex: '1 1 220px',
  minWidth: 180,
});

export const SearchIconWrap = styled(Box)({
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  color: FILTER_PLACEHOLDER,
  pointerEvents: 'none',
});

export const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 40,
  borderRadius: 10,
  border: `1px solid ${FILTER_FIELD_BORDER}`,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  paddingLeft: 36,
  paddingRight: 12,
  fontSize: 14,
  fontFamily: 'inherit',
  color: theme.palette.text.primary,
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: FILTER_PLACEHOLDER },
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}26`,
  },
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
  minWidth: 160,
  height: 40,
  fontSize: 14,
  fontWeight: 500,
  flexShrink: 0,
  borderRadius: 10,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 14,
    paddingRight: '26px !important',
    paddingTop: 0,
    paddingBottom: 0,
    height: '40px !important',
  },
  '& .MuiSelect-icon': {
    width: 16,
    height: 16,
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#8782A0',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(34, 28, 90, 0.12)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}80`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main}99`,
    borderWidth: 2,
  },
})) as unknown as <T = unknown>(props: SelectProps<T>) => ReactElement;

export const ActionCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

export const SearchAdornmentIcon = styled(SvgIcon)(({ theme }) => ({
  color: theme.palette.text.disabled,
  marginRight: theme.spacing(0.5),
  fontSize: '1.1rem',
}));

export const ClearFiltersLink = styled('button')(({ theme }) => ({
  flexShrink: 0,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: theme.palette.text.disabled,
  transition: 'color 0.15s ease',
  '&:hover': { color: theme.palette.text.primary },
}));

export const ApplyButton = styled(Button)(({ theme }) => ({
  height: 32,
  fontWeight: 600,
  fontSize: 12,
  borderRadius: 10,
  flexShrink: 0,
  paddingLeft: 12,
  paddingRight: 12,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    color: '#fff',
    background: theme.palette.action.disabledBackground,
  },
}));

export const AddInput = styled(InputBase)(({ theme }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  flexGrow: 1,
  fontSize: 14,
}));

/** Hero title */
export const HeroTitle = styled('h1')({
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.3,
});

/** Hero subtitle */
export const HeroSubtitle = styled('p')({
  margin: '4px 0 0',
  fontSize: 13,
  color: 'rgba(255,255,255,0.7)',
});

export const HeroAddButton = HeroActionButton;

export const DeleteMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 13,
}));

export const DeleteListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

export const FormFieldStack = styled(Stack)({
  paddingTop: 4,
});

export const FormDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

export const FormDialogContent = styled(DialogContent)({
  paddingTop: 0,
});

// ─── Form field shared styles ─────────────────────────────────────────────────

// ─── ClientFormModal styled components ───────────────────────────────────────

export const ModalDialog = styled(Dialog)({
  '& .MuiPaper-root': { borderRadius: '16px' },
});

export const ModalTitle = styled(DialogTitle)({
  fontSize: 16,
  fontWeight: 700,
  paddingBottom: 8,
});

export const ModalCancelButton = styled(Button)({
  fontSize: 13,
  textTransform: 'none',
  borderRadius: '8px',
});

export const ModalSubmitButton = styled(Button)({
  fontSize: 13,
  textTransform: 'none',
  borderRadius: '8px',
  fontWeight: 600,
});

// ─── ClientsPage hero banner layout ──────────────────────────────────────────

export const HeroBannerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: 0,
});

export const HeroBannerTextBox = styled(Box)({ minWidth: 0 });

export const HeroBannerButtonBox = styled(Box)({ marginLeft: 16 });

// ─── Filter status dropdown menu constants ────────────────────────────────────

export const STATUS_MENU_PAPER_SX = {
  mt: 0.5,
  minWidth: 130,
  borderRadius: '10px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  border: '1px solid rgba(0,0,0,0.07)',
  py: '4px',
};

export const STATUS_MENU_LIST_SX = { py: 0 };

export const STATUS_MENU_ITEM_SX = {
  fontSize: 13,
  fontWeight: 500,
  px: 1.5,
  py: '5px',
  minHeight: 0,
  textTransform: 'capitalize',
  borderRadius: '6px',
  mx: '4px',
  '&.Mui-selected': {
    backgroundColor: 'secondary.light',
    '&:hover': { backgroundColor: 'secondary.light' },
  },
};

export const FORM_FIELD_SX = {
  '& .MuiInputBase-input': { fontSize: 13 },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiFormHelperText-root': { fontSize: 11 },
};

export const FORM_MENU_ITEM_SX = {
  fontSize: 13,
  fontWeight: 500,
  px: 1.5,
  py: '5px',
  minHeight: 0,
  borderRadius: '6px',
  mx: '4px',
  '&.Mui-selected': { backgroundColor: 'secondary.light' },
  '&.Mui-selected:hover': { backgroundColor: 'secondary.light' },
};

export const FORM_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: '10px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
      border: '1px solid rgba(0,0,0,0.07)',
      py: '4px',
    },
  },
  MenuListProps: { sx: { py: 0 } },
};

// ─── Row menu sizing (shared with tracker pattern) ────────────────────────────

export const CLIENTS_MENU_PAPER_SX = {
  minWidth: 148,
  borderRadius: '10px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  border: '1px solid rgba(0,0,0,0.07)',
  py: '4px',
};

export const CLIENTS_MENU_ITEM_SX = {
  px: 1.5,
  py: '5px',
  minHeight: 0,
  fontSize: 13,
  fontWeight: 500,
};

export const CLIENTS_MENU_TEXT_SX = { fontSize: 13, fontWeight: 500 };

// ─── ClientSelect styled components ──────────────────────────────────────────

const FORM_FIELD_BG = 'rgba(248, 246, 253, 0.6)';
const FORM_FIELD_BORDER = 'rgba(34, 28, 90, 0.10)';

export const ClientSelectTrigger = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: 40,
  padding: '0 12px',
  border: `1px solid ${FORM_FIELD_BORDER}`,
  borderRadius: 10,
  backgroundColor: FORM_FIELD_BG,
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:hover': { borderColor: `${theme.palette.primary.main}40` },
  '&[data-open="true"]': {
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
    boxShadow: `0 0 0 4px ${theme.palette.secondary.main}1F`,
  },
}));

export const ClientSelectChevron = styled('span')<{ open: boolean }>(({ open }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  transition: 'transform 0.2s',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  color: '#8782A0',
  fontSize: 16,
  marginLeft: 4,
}));

export const ClientSelectPopover = styled(Paper)(({ theme }) => ({
  width: 'var(--trigger-width)',
  borderRadius: 10,
  border: `1px solid rgba(0,0,0,0.07)`,
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  overflow: 'hidden',
  marginTop: 4,
  backgroundColor: theme.palette.background.paper,
}));

export const ClientSelectSearchBox = styled(InputBase)(({ theme }) => ({
  width: '100%',
  padding: '8px 14px',
  fontSize: 13,
  borderBottom: `1px solid rgba(0,0,0,0.07)`,
  '& input': { padding: 0 },
  '& input::placeholder': { color: theme.palette.text.disabled },
}));

export const ClientSelectList = styled('div')({
  maxHeight: 240,
  overflowY: 'auto',
  padding: '4px',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(47,39,117,0.2) transparent',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(47,39,117,0.2)', borderRadius: 4 },
});

export const ClientSelectItem = styled('div')<{ selected?: boolean }>(({ theme, selected }) => ({
  fontSize: 13,
  fontWeight: 500,
  padding: '6px 10px',
  borderRadius: 6,
  cursor: 'pointer',
  color: theme.palette.text.primary,
  backgroundColor: selected ? 'rgba(0,0,0,0.06)' : 'transparent',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
}));

export const ClientSelectEmpty = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.disabled,
  padding: '10px 14px',
  textAlign: 'center',
}));

// ─── Client list row — name cell with hover-reveal arrow ─────────────────────

export const ClientNameCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
});

export const ClientNameText = styled('span')({
  fontWeight: 600,
  fontSize: 13,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

/** Chevron beside a client name — hidden until the containing table row is hovered. */
export const ClientRowArrow = styled(ChevronRight)({
  flexShrink: 0,
  color: '#8782A0',
  opacity: 0,
  transform: 'translateX(0px)',
  transition: 'opacity 0.15s ease, transform 0.15s ease',
  '.MuiTableRow-root:hover &': {
    opacity: 1,
    transform: 'translateX(2px)',
  },
});

// ─── Client Detail Page — hero + tabs (mirrors ProjectDetailPage's shell) ─────

// Matches the design's `space-y-4` (16px) between Hero / TabBar / tab content.
export const ClientDetailPageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 64,
});

// `px-6 py-7 sm:px-8 sm:py-8` — desktop-first, since the app shell is desktop-only in practice.
export const DetailHero = styled(HeroSection)({
  padding: 32,
  boxShadow: '0 30px 80px -32px rgba(49,46,129,0.65)',
});

// Three colored glow blobs + a faint dot-grid texture — replaces the shared
// (plain white) HeroDecorCircle* for this page only, matching the reference exactly.
export const DetailHeroBlobTopRight = styled(Box)({
  position: 'absolute',
  right: -64,
  top: -96,
  height: 288,
  width: 288,
  borderRadius: '50%',
  backgroundColor: 'rgba(177, 151, 255, 0.3)',
  filter: 'blur(90px)',
  pointerEvents: 'none',
});

export const DetailHeroBlobMid = styled(Box)({
  position: 'absolute',
  right: '33.333%',
  top: '50%',
  height: 256,
  width: 256,
  borderRadius: '50%',
  backgroundColor: 'rgba(91, 141, 239, 0.25)',
  filter: 'blur(90px)',
  pointerEvents: 'none',
});

export const DetailHeroBlobBottomLeft = styled(Box)({
  position: 'absolute',
  bottom: -96,
  left: '25%',
  height: 256,
  width: 256,
  borderRadius: '50%',
  backgroundColor: 'rgba(122, 77, 255, 0.25)',
  filter: 'blur(90px)',
  pointerEvents: 'none',
});

export const DetailHeroDotTexture = styled(Box)({
  position: 'absolute',
  inset: 0,
  opacity: 0.06,
  backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
  backgroundSize: '22px 22px',
  pointerEvents: 'none',
});

// No uniform gap here — each child sets its own margin-top, matching the
// design's individual `mt-*` utilities (they aren't uniform: 16px then 24px).
export const DetailHeroContent = styled(Box)({
  position: 'relative',
});

export const DetailHeroBreadcrumbs = styled(Breadcrumbs)({
  color: 'rgba(255,255,255,0.6)',
  fontSize: '0.875rem',
  '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.4)' },
});

export const DetailHeroBreadcrumbLink = styled(Link)({
  cursor: 'pointer',
  color: 'rgba(255,255,255,0.6)',
  '&:hover': { color: '#fff' },
});

// `mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between`
export const DetailHeroTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginTop: 16,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
}));

export const DetailHeroNameColumn = styled(Box)({
  minWidth: 0,
});

export const DetailHeroTitle = styled(Typography)({
  fontSize: '1.875rem',
  fontWeight: 600,
  lineHeight: 1.2,
  color: '#fff',
  letterSpacing: '-0.02em',
});

// Project Details hero only: `flex flex-wrap items-center gap-2.5` — the status
// chip sits inline with the title here, unlike the Client hero (DetailHeroPillsRow),
// where it sits in its own row below the subtitle together with the "since" text.
export const DetailHeroTitleStatusRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 10,
});

export const DetailHeroSubtitle = styled(Typography)({
  fontSize: '0.875rem',
  color: 'rgba(255,255,255,0.65)',
  marginTop: 6,
});

const HERO_PILL_DOT: Record<string, string> = {
  Active: '#6EE7B7',
  OnHold: '#FCD34D',
  Inactive: 'rgba(255,255,255,0.45)',
};

export const DetailHeroPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'muted' && prop !== 'clientStatus',
})<{ muted?: boolean; clientStatus?: string }>(({ muted }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 9999,
  padding: '2px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: muted ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
}));

export const DetailHeroPillDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'muted' && prop !== 'dotStatus',
})<{ muted?: boolean; dotStatus?: string }>(({ dotStatus }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: HERO_PILL_DOT[dotStatus ?? ''] ?? 'rgba(255,255,255,0.45)',
}));

// Project Details hero only: design's ProjectStatusChip, rendered here with its
// `!bg-white/20 !text-white !ring-white/25` hero override — a constant translucent
// background + a ring border for every status (unlike DetailHeroPill's Client-hero
// dimmed/undimmed background), at the chip's own smaller 11px/4px-gap sizing.
// Reuses DetailHeroPillDot as-is — the dot itself is the same 6px size either way.
export const ProjectHeroStatusPill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 9999,
  padding: '2px 10px',
  fontSize: '0.6875rem',
  lineHeight: '16px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: 'rgba(255,255,255,0.2)',
  border: '1px solid rgba(255,255,255,0.25)',
});

// The shared HeroActionButton (shared/components/HeroContainer, also used by /projects,
// /team, and the timer bar) doesn't match this design's `min-h-[46px] rounded-[14px]
// shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)]` primary hero CTA spec — those other pages'
// own buttons weren't audited against this design and may intentionally differ, so this
// is a page-local override rather than a change to the shared component.
export const ProjectHeroActionButton = styled(HeroActionButton)({
  minHeight: 46,
  height: 'auto',
  borderRadius: 14,
  boxShadow: '0 8px 24px -8px rgba(0,0,0,0.4)',
});

// `mt-2.5 flex flex-wrap items-center gap-2 text-xs text-white/60`
export const DetailHeroPillsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginTop: 10,
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.6)',
});

export const DetailHeroSinceText = styled('span')({
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.6)',
});

// `flex flex-wrap items-center gap-3`
export const DetailHeroActionsRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
});

// `mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3`
export const ClientKpiGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gap: 12,
  marginTop: 24,
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

// ─── Hero KPI tiles (Projects / Members / Billing Hours) ─────────────────────
// Bespoke — the shared HeroStatCard/HeroStatDot primitives put the dot next to
// the value and uppercase the label, which doesn't match this design: here the
// dot sits beside the (normal-case) label, and the value is a plain bold number.

export const ClientKpiCard = styled(Box)({
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.1)',
  backgroundColor: 'rgba(255,255,255,0.08)',
  backdropFilter: 'blur(4px)',
  padding: '14px 16px',
});

export const ClientKpiLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const ClientKpiDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'dotcolor',
})<{ dotcolor: string }>(({ dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotcolor,
  flexShrink: 0,
}));

export const ClientKpiLabel = styled('span')({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'rgba(255,255,255,0.7)',
});

export const ClientKpiLabelSub = styled('span')({
  color: 'rgba(255,255,255,0.45)',
});

export const ClientKpiValue = styled('span')({
  display: 'block',
  marginTop: 8,
  fontSize: '1.875rem',
  fontWeight: 700,
  lineHeight: 1,
  color: '#fff',
  fontVariantNumeric: 'tabular-nums',
});

/** Glassy icon-only button for hero secondary actions (e.g. the "…" menu trigger). */
export const HeroGlassIconButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 46,
  height: 46,
  flexShrink: 0,
  borderRadius: 14,
  border: '1px solid rgba(255,255,255,0.2)',
  backgroundColor: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(4px)',
  color: '#fff',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
});

// Matches the design's TabBar: `rounded-[14px] bg-surface-2 p-1` track with a
// `gap-1` row of buttons. Vertical spacing from siblings comes from the page's
// own flex gap (ClientDetailPageRoot), not a margin on this component.
export const PillTabsWrapper = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 40,
  width: 'fit-content',
  borderRadius: 14,
  backgroundColor: '#F8F6FD',
  padding: 4,
});

export const PillTab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  borderRadius: 10,
  padding: '6px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  backgroundColor: active ? theme.palette.background.paper : 'transparent',
  color: active ? theme.palette.text.primary : theme.palette.text.disabled,
  boxShadow: active ? '0 1px 2px rgba(16,16,40,0.08), 0 2px 6px -2px rgba(16,16,40,0.12)' : 'none',
  '&:hover': {
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  },
}));

export const PillTabCount = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'inline-flex',
  height: 16,
  minWidth: 16,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 9999,
  padding: '0 4px',
  fontSize: '0.625rem',
  fontWeight: 700,
  backgroundColor: active ? '#F8F6FD' : 'rgba(0,0,0,0.05)',
  color: active ? '#5A5578' : '#8782A0',
}));

export const TabCardShell = styled(Paper)({
  border: '1px solid rgba(34, 28, 90, 0.07)',
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  overflow: 'hidden',
  padding: '32px 24px',
  textAlign: 'center',
});

// ─── Client Detail hero "more actions" menu ───────────────────────────────────
// Matches the design's DropdownMenu exactly — distinct from CLIENTS_MENU_*
// above (the list page's compact row kebab, a different, already-approved spec).

export const DETAIL_ACTIONS_MENU_PAPER_SX = {
  mt: 1,
  minWidth: 160,
  borderRadius: '14px',
  border: '1px solid rgba(34,28,90,0.07)',
  backgroundColor: '#fff',
  backgroundImage: 'none',
  boxShadow: '0 8px 32px rgba(15,23,42,0.12)',
  p: '4px',
};

// `flex w-full items-center gap-2 rounded-[8px] px-2 py-1.5 text-sm` — display/gap
// are set explicitly (not left to MenuItem's own default) since the icon and
// label are rendered as plain children here, not via ListItemIcon/ListItemText.
export const DETAIL_ACTIONS_MENU_ITEM_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  cursor: 'default',
  borderRadius: '8px',
  px: 1,
  py: 0.75,
  minHeight: 0,
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
};

// Applied only to the two non-destructive items — kept separate from
// DETAIL_ACTIONS_MENU_ITEM_SX so it never collides with
// DetailActionsDeleteMenuItem's own (red) hover state below.
export const DETAIL_ACTIONS_MENU_ITEM_HOVER_SX = {
  '&:hover, &.Mui-focusVisible': { backgroundColor: '#F8F6FD' },
};

export const DetailActionsDeleteMenuItem = styled(MenuItem)({
  color: '#BE3737',
  '&:hover, &.Mui-focusVisible': { backgroundColor: 'rgba(190,55,55,0.1)' },
});

// Non-destructive item + its hover state + the text color, combined once so
// callers don't spread three pieces inline at every use site.
export const DETAIL_ACTIONS_MENU_ITEM_ACTIVE_SX = {
  ...DETAIL_ACTIONS_MENU_ITEM_SX,
  ...DETAIL_ACTIONS_MENU_ITEM_HOVER_SX,
  color: '#141030',
};

export const DETAIL_ACTIONS_MENU_LIST_SX = {
  '& .MuiList-root': { padding: 0 },
};

export const DETAIL_ACTIONS_DIVIDER_SX = {
  mx: -0.5,
  my: 0.5,
  borderColor: 'rgba(34,28,90,0.07)',
};

export const DetailHeroBreadcrumbSeparatorIcon = styled(ChevronRightIcon)({
  fontSize: 14,
});

// A plain sx object, not a styled(Typography) — styled() loses Typography's
// polymorphic `component` prop typing, and this is always rendered as a button.
export const NOT_FOUND_BACK_LINK_SX = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  color: 'primary.main',
  textDecoration: 'underline',
};

// ─── Client Detail Page — Projects tab ────────────────────────────────────────

// Status pill for the Projects tab table — real projects only have two states
// (Active/Archived), but the design's chip includes a leading dot that the
// reused /projects `StatusChip` doesn't have, so this is a small local variant.
const CLIENT_PROJECT_STATUS_TINTS = {
  active: { bg: 'rgba(45, 140, 100, 0.1)', fg: '#2D8C64' },
  onhold: { bg: 'rgba(245, 158, 11, 0.1)', fg: '#b45309' },
  archived: { bg: '#F8F6FD', fg: '#8782A0' },
};

export const ClientProjectStatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: 'active' | 'onhold' | 'archived' }>(({ status }) => {
  const tint = CLIENT_PROJECT_STATUS_TINTS[status] ?? CLIENT_PROJECT_STATUS_TINTS.archived;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '0.625rem',
    fontWeight: 600,
    backgroundColor: tint.bg,
    color: tint.fg,
  };
});

export const ClientProjectStatusDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: 'active' | 'onhold' | 'archived' }>(({ status }) => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: (CLIENT_PROJECT_STATUS_TINTS[status] ?? CLIENT_PROJECT_STATUS_TINTS.archived).fg,
}));

/** Row-actions cell that only appears on hover, matching the reference design. */
// `active` forces the hover-reveal opacity on even when the row isn't hovered — needed
// while this row's "…" menu/dropdown is open, since opening it (e.g. the Add Member
// picker) moves the pointer off the row, which would otherwise fade the trigger back out
// mid-interaction and make it look disabled.
export const ProjectRowActions = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  opacity: active ? 1 : 0,
  transition: 'opacity 0.15s ease',
  '.MuiTableRow-root:hover &': {
    opacity: 1,
  },
}));

/** Matches the design's `space-y-3` (12px) between this tab's own Toolbar and table —
 * distinct from the page-level 16px gap between Hero/TabBar/tab content. */
export const ClientTabContentStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

// ─── Client Detail Page — Members tab ─────────────────────────────────────────

// Design's MemberStatusChip uses its success/warning tokens here (green/amber),
// not the muted gray the /projects Access tab's MemberStatusBadge uses for its
// own inactive state — so this tab needs its own tint, not the reused one.
const CLIENT_MEMBER_STATUS_TINTS: Record<string, { bg: string; fg: string }> = {
  Active: { bg: 'rgba(45, 140, 100, 0.1)', fg: '#2D8C64' },
  Inactive: { bg: 'rgba(245, 158, 11, 0.12)', fg: '#b45309' },
  Exited: { bg: 'rgba(148, 163, 184, 0.15)', fg: '#64748b' },
};

export const ClientMemberStatusBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'memberLabel',
})<{ active?: boolean; memberLabel?: string }>(({ memberLabel }) => {
  const tint = CLIENT_MEMBER_STATUS_TINTS[memberLabel ?? ''] ?? CLIENT_MEMBER_STATUS_TINTS.Exited;
  return {
    height: 18,
    borderRadius: 9999,
    fontSize: '0.625rem',
    fontWeight: 600,
    backgroundColor: tint.bg,
    color: tint.fg,
  };
});

// Table cell edge-compensation — same 14px/32px outer-edge values ClientProjectsTab's
// own Projects table columns use, for the identical reason (cellPaddingX alone can't
// distinguish the row's outer edge from the gap between columns).
export const MEMBERS_CELL_PAD_LEFT_SX = { pl: '14px' };
export const MEMBERS_CELL_PAD_RIGHT_SX = { pr: '14px' };
export const MEMBERS_CELL_PAD_RIGHT_WIDE_SX = { pr: '32px' };
export const MEMBERS_TOTAL_CELL_SX = { pr: '32px', whiteSpace: 'nowrap', fontWeight: 500 };
export const MEMBERS_STATUS_CELL_SX = { display: 'inline-block', pr: '14px' };
export const MEMBERS_SECONDARY_TEXT_BLOCK_SX = { display: 'block' };
export const MEMBERS_SEARCH_ICON_SX = { fontSize: 16 };

export const MEMBERS_TABLE_CONTAINER_SX = {
  border: '1px solid rgba(34,28,90,0.07)',
  borderRadius: '14px',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
};

// Filter toolbar — the compact padding override this tab (and the Projects tab) use
// instead of FilterToolbarCard's own default padding, plus each filter's fixed width.
export const MEMBERS_TOOLBAR_SX = { padding: '10px 12px', gap: 1 };
export const MEMBERS_ROLE_FILTER_SX = { width: 160, minWidth: 160, flexShrink: 0 };
export const MEMBERS_STATUS_FILTER_SX = { width: 128, minWidth: 128, flexShrink: 0 };
export const MEMBERS_PROJECT_FILTER_SX = {
  width: 160,
  minWidth: 160,
  flexShrink: 0,
  '& .MuiSelect-select': { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
};

// ─── Project Details page — hero KPI grid + Members section ─────────────────

export const PROJECT_KPI_GRID_SX = {
  gridTemplateColumns: { sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
};

export const PROJECT_MEMBERS_MAPPED_SINCE_SX = {
  fontSize: '0.75rem',
  color: 'text.disabled',
  whiteSpace: 'nowrap',
};
export const PROJECT_MEMBERS_ROW_ACTIONS_SX = { pr: '10px' };
export const PROJECT_MEMBERS_TABS_WRAPPER_SX = {
  width: '100%',
  overflowX: 'auto',
  justifyContent: 'flex-start',
};

// ─── Member drawer (per-member hours-per-project breakdown) ──────────────────

// Design's own text tokens (verified against a live render of the reference app:
// text-tertiary = #8782A0, text-secondary = #5A5578). These are purple-tinted greys,
// NOT MUI's neutral text.disabled/text.secondary — and they're already hardcoded this
// way elsewhere in this feature, so the drawer uses the same literals for consistency.
const DRAWER_TEXT_TERTIARY = '#8782A0';
const DRAWER_TEXT_SECONDARY = '#5A5578';
const DRAWER_TEXT_PRIMARY = '#141030';

export const MEMBER_DRAWER_BACKDROP_SX = {
  backgroundColor: 'rgba(27,18,64,0.4)',
  backdropFilter: 'blur(2px)',
};

export const MEMBER_DRAWER_PAPER_SX = {
  width: 460,
  maxWidth: '100%',
  boxShadow: '0 20px 60px -20px rgba(27,18,64,0.55)',
  display: 'flex',
  flexDirection: 'column',
};

export const DrawerHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  borderBottom: '1px solid rgba(34,28,90,0.08)',
  padding: '16px 20px',
});

export const DrawerTitle = styled(Typography)({
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '-0.025em',
  color: DRAWER_TEXT_PRIMARY,
});

export const DrawerCloseButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  marginRight: -4,
  borderRadius: '10px',
  flexShrink: 0,
  color: DRAWER_TEXT_TERTIARY,
  '&:hover': {
    backgroundColor: 'rgba(110, 79, 247, 0.08)',
    color: theme.palette.text.primary,
  },
}));

export const DrawerBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '16px 20px',
});

export const DrawerContentStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const DrawerIdentityRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

// fontSize matches design's MemberAvatar formula exactly: size * 0.36 = 44 * 0.36 = 15.84px.
export const DRAWER_AVATAR_SX = { width: 44, height: 44, fontSize: '15.84px' };

export const DrawerBadgeRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
});

export const DrawerEmailText = styled(Typography)({
  display: 'block',
  marginTop: 4,
  lineHeight: '1rem',
  color: DRAWER_TEXT_TERTIARY,
});

/** Role label in the drawer's identity row — design's text-secondary, not MUI's. */
export const DrawerRoleText = styled(Typography)({
  color: DRAWER_TEXT_SECONDARY,
});

// The drawer header's own subtitle ("Projects for {client}") — a slightly tighter
// mt than DrawerEmailText's, matching the design's distinct spacing there.
export const DRAWER_SUBTITLE_SX = {
  display: 'block',
  mt: 0.25,
  lineHeight: '1rem',
  color: DRAWER_TEXT_TERTIARY,
};

export const DrawerStatsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 8,
});

/** Same tile grid as DrawerStatsGrid, 3 columns — the Project Details member drawer's
 * This Week / This Month / Overall row (design: `grid-cols-3 gap-2`). */
export const DrawerStatsGrid3 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 8,
});

// Shared by the Total/Projects stat tiles and each per-project breakdown card below —
// identical border/radius/padding in all three places in the design.
export const DrawerCard = styled(Box)({
  border: '1px solid rgba(34,28,90,0.07)',
  borderRadius: '12px',
  padding: 12,
  backgroundColor: '#fff',
});

// lineHeight 1.5 here is not a stylistic choice — it's the literal measured value
// (verified via a real render) of design's arbitrary `text-[10px]` label, which has
// no bundled Tailwind line-height and inherits Preflight's root `line-height: 1.5`.
// `display: block` is load-bearing, not cosmetic: this renders via Typography
// variant="caption" → a <span>, and as an inline element the card's own 24px
// line-height strut (body 16px x 1.5) would dominate the line box, adding ~9px of
// dead vertical space the 1.5 line-height below can't shrink. Design's equivalent
// is a <p> (block), so the label box must be block here to match its 15px height.
export const DrawerStatLabel = styled(Typography)({
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  fontWeight: 500,
  color: DRAWER_TEXT_TERTIARY,
  fontSize: '0.625rem',
  lineHeight: 1.5,
});

// accent uses the workspace's theme color (like the hero and primary CTAs), not a fixed
// purple — it's a highlighted stat callout, not a fixed design-system tint like the avatar.
export const DrawerStatValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'accent',
})<{ accent?: boolean }>(({ accent, theme }) => ({
  marginTop: 4,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: accent ? theme.palette.primary.main : DRAWER_TEXT_PRIMARY,
}));

// Same measured-not-guessed 1.5 line-height as DrawerStatLabel (arbitrary text-[11px]
// inheriting Preflight's root line-height, same as the 10px label measured above).
export const DrawerSectionLabel = styled(Typography)({
  display: 'block',
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  color: DRAWER_TEXT_TERTIARY,
  fontWeight: 600,
  fontSize: '0.6875rem',
  lineHeight: 1.5,
});

export const DrawerProjectList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const DrawerProjectRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

export const DrawerProjectHours = styled(Typography)({
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
});

export const DrawerProjectBar = styled(LinearProgress)({
  marginTop: 8,
  height: 6,
  borderRadius: 9999,
  backgroundColor: 'rgba(110, 79, 247, 0.1)',
  '& .MuiLinearProgress-bar': { backgroundColor: '#6E4FF7', borderRadius: 9999 },
});

// Same measured-not-guessed 1.5 line-height as DrawerStatLabel/DrawerSectionLabel.
export const DrawerProjectPercentText = styled(Typography)({
  display: 'block',
  marginTop: 4,
  fontSize: '0.6875rem',
  lineHeight: 1.5,
  color: DRAWER_TEXT_TERTIARY,
});

// ─── Project Details member drawer only — billable split + recent entries ────
// (design's MemberDrawer.tsx, distinct from the Client Members drawer above)

const DRAWER_SUCCESS = '#2D8C64'; // same green already used for the Active status tint

export const BillableSplitCard = styled(Box)({
  border: '1px solid rgba(34,28,90,0.07)',
  borderRadius: '14px',
  backgroundColor: 'rgba(248,246,253,0.4)',
  padding: 16,
});

export const BillableSplitHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
  fontSize: '0.75rem',
  lineHeight: '1rem',
});

export const BillableLabel = styled('span')({
  fontWeight: 500,
  color: DRAWER_SUCCESS,
});

export const NonBillableLabel = styled('span')({
  fontWeight: 500,
  color: DRAWER_TEXT_TERTIARY,
});

export const BillableBarTrack = styled(Box)({
  display: 'flex',
  height: 10,
  width: '100%',
  overflow: 'hidden',
  borderRadius: 9999,
  backgroundColor: '#F8F6FD',
});

export const BillableBarFill = styled('span')({
  display: 'block',
  height: '100%',
  backgroundColor: DRAWER_SUCCESS,
});

export const BillableBarRemainder = styled('span')({
  display: 'block',
  height: '100%',
  backgroundColor: 'rgba(110, 79, 247, 0.25)',
});

export const RecentEntriesCard = styled(Box)({
  overflow: 'hidden',
  borderRadius: '14px',
  border: '1px solid rgba(34,28,90,0.07)',
  '& > *:not(:last-child)': {
    borderBottom: '1px solid rgba(34,28,90,0.06)',
  },
});

export const RecentEntryRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 14px',
});

export const RecentEntryTaskText = styled(Typography)({
  fontWeight: 500,
  color: DRAWER_TEXT_PRIMARY,
});

export const RecentEntryDateText = styled(Typography)({
  display: 'block',
  marginTop: 2,
  fontSize: '0.6875rem',
  lineHeight: 1.5,
  color: DRAWER_TEXT_TERTIARY,
});

export const RecentEntryDuration = styled(Typography)({
  flexShrink: 0,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  color: DRAWER_TEXT_PRIMARY,
});

export const RecentEntryBillableTag = styled('span', {
  shouldForwardProp: (prop) => prop !== 'billable',
})<{ billable?: boolean }>(({ billable }) => ({
  display: 'block',
  width: 92,
  flexShrink: 0,
  textAlign: 'right',
  fontSize: '0.6875rem',
  fontWeight: 500,
  color: billable ? DRAWER_SUCCESS : DRAWER_TEXT_TERTIARY,
}));

export const ViewTimesheetButton = styled('button')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: 'none',
  borderRadius: '10px',
  padding: '10px 16px',
  fontFamily: 'inherit',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 600,
  color: '#fff',
  cursor: 'pointer',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  transition: 'opacity 0.15s ease',
  '&:hover': { opacity: 0.95 },
}));

export const DrawerFooter = styled(Box)({
  borderTop: '1px solid rgba(34,28,90,0.08)',
  padding: '14px 20px',
});
